import React, { useMemo, useEffect } from 'react';
import { RateCardRateBase, Currencies, RateCardTermType } from '@bringg/types';

import { InputNumber, Select } from '../../../../components';
import { DEFAULT_PRECISION } from '../consts';
import type { RateCartTermUpdater } from '../types';
import { Condition } from 'features/conditions/rule/rule-utils';
import { getAdjustedRateBaseWithOptions } from '../utils/rate-base-utils';

interface RateCardTermRatePros {
	titleText: string;
	disabled?: boolean;
	rateBase?: RateCardRateBase;
	rateType?: RateCardTermType;
	rateValue?: number;
	rateCurrency?: string;
	conditions: Condition[];
	updateTerm?(updater: Partial<RateCartTermUpdater>): void;
	getPopupContainer?(target: HTMLElement): HTMLElement;
}

export const RateCardTermRate = ({
	titleText,
	disabled = false,
	rateBase,
	conditions,
	rateValue,
	rateType,
	rateCurrency = 'usd',
	updateTerm,
	getPopupContainer
}: RateCardTermRatePros) => {
	const [onRateBaseChange, onRateValueChange] = useMemo(
		() =>
			updateTerm
				? [
						(rate_base: RateCardRateBase) => updateTerm({ rate_base }),
						(rate_amount: number) => updateTerm({ rate_amount })
				  ]
				: [],
		[updateTerm]
	);

	const { adjustedRateBase, rateBaseOptions } = useMemo(() => {
		return getAdjustedRateBaseWithOptions(conditions, rateBase, rateType);
	}, [conditions, rateBase, rateType]);

	// Trigger onRateBaseChange when adjustedRateBase changes due to condition changes
	// TODO: Consider redesign handling of rate base changes to avoid this kind of side effects
	useEffect(() => {
		if (onRateBaseChange && adjustedRateBase !== rateBase) {
			onRateBaseChange(adjustedRateBase);
		}
	}, [adjustedRateBase, onRateBaseChange, rateBase]);

	const currencySign = Currencies[String(rateCurrency).toUpperCase()]?.symbol;
	const title = `${titleText} (${currencySign})`;

	return (
		<>
			<div className="rate-header" title={title}>
				{title}
			</div>
			<div className="rate-data-wrapper">
				<Select
					data-test-id="rate-base-select"
					disabled={disabled || !onRateBaseChange}
					value={adjustedRateBase}
					options={rateBaseOptions}
					onChange={onRateBaseChange}
					getPopupContainer={getPopupContainer}
				/>
				<span className="rate-value-relation-sign">
					{adjustedRateBase === RateCardRateBase.Flat ? '=' : 'X'}
				</span>
				<InputNumber
					data-test-id="rate-value-input"
					disabled={disabled}
					value={rateValue}
					onChange={v => onRateValueChange(Number(v))}
					precision={adjustedRateBase === RateCardRateBase.Flat ? DEFAULT_PRECISION : undefined}
				/>
			</div>
		</>
	);
};
